import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { PROJECT_ID } from '@/lib/project';
import { useNoindex } from '@/hooks/useNoindex';
import { useToast } from '@/components/base/Toast';

interface EmailConfig {
  id: number;
  rdy_project_id: string;
  to: string | null;
  cc: string | null;
  bcc: string | null;
  subject: string | null;
  message: string | null;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function AdminEmailSettings() {
  useNoindex();
  const { showToast } = useToast();
  const [config, setConfig] = useState<EmailConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { data, error } = await supabase
        .from('contact_form_configs')
        .select('*')
        .eq('rdy_project_id', PROJECT_ID)
        .maybeSingle();
      if (error) throw new Error(error.message);
      const row = (data as EmailConfig) || null;
      setConfig(row);
      setTo(row?.to || '');
      setCc(row?.cc || '');
      setBcc(row?.bcc || '');
    } catch (err) {
      setLoadError((err as Error).message || '設定の取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (!to.trim()) {
      errors.to = '主送信先メールアドレスは必須です。';
    } else if (!EMAIL_PATTERN.test(to.trim())) {
      errors.to = '有効なメールアドレスを入力してください。';
    }
    if (cc.trim() && !EMAIL_PATTERN.test(cc.trim())) {
      errors.cc = '有効なメールアドレスを入力してください。';
    }
    if (bcc.trim() && !EMAIL_PATTERN.test(bcc.trim())) {
      errors.bcc = '有効なメールアドレスを入力してください。';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('contact_form_configs')
        .update({
          to: to.trim(),
          cc: cc.trim() || null,
          bcc: bcc.trim() || null,
        })
        .eq('rdy_project_id', PROJECT_ID);
      if (error) throw new Error(error.message);
      setConfig((prev) => (prev ? { ...prev, to: to.trim(), cc: cc.trim(), bcc: bcc.trim() } : prev));
      showToast('送信先メールアドレスを更新しました', 'success');
    } catch {
      showToast('更新に失敗しました。もう一度お試しください。', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-3 text-foreground-500">
        <i className="ri-loader-4-line animate-spin text-2xl" />
        <span className="text-sm">読み込み中...</span>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-3 text-center">
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-100 text-accent-700">
          <i className="ri-error-warning-line text-lg" />
        </span>
        <p className="text-sm text-foreground-700">{loadError}</p>
        <button
          type="button"
          onClick={load}
          className="rounded-md bg-primary-500 px-4 py-2 text-sm text-background-50 hover:bg-primary-600"
        >
          再試行
        </button>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-2 rounded-lg border border-background-200 bg-background-50 text-center text-foreground-500">
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-background-200">
          <i className="ri-mail-settings-line text-lg" />
        </span>
        <p className="text-sm">メール設定が見つかりませんでした。</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      <form
        onSubmit={handleSave}
        className="space-y-5 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
      >
        <div>
          <h2 className="font-heading text-sm font-semibold">送信先メールアドレス</h2>
          <p className="mt-1 text-xs text-foreground-500">
            フォーム送信時の通知先です。主送信先は必須、CC / BCC は任意です。
          </p>
        </div>

        <Field
          id="to"
          label="主送信先 (To)"
          required
          value={to}
          onChange={setTo}
          error={fieldErrors.to}
          placeholder="contact@example.com"
        />
        <Field
          id="cc"
          label="CC"
          value={cc}
          onChange={setCc}
          error={fieldErrors.cc}
          placeholder="（任意）"
        />
        <Field
          id="bcc"
          label="BCC"
          value={bcc}
          onChange={setBcc}
          error={fieldErrors.bcc}
          placeholder="（任意）"
        />

        <div className="flex justify-end pt-1">
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? (
              <>
                <i className="ri-loader-4-line animate-spin" />
                保存中...
              </>
            ) : (
              <>
                <i className="ri-save-3-line" />
                保存する
              </>
            )}
          </button>
        </div>
      </form>

      <div className="space-y-4 rounded-lg border border-background-200 bg-background-100/50 p-5 md:p-6">
        <div className="flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
            <i className="ri-file-text-line text-sm" />
          </span>
          <h2 className="font-heading text-sm font-semibold">メールテンプレート（参照用・編集不可）</h2>
        </div>

        <div>
          <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-foreground-500">
            件名テンプレート
          </p>
          <pre className="whitespace-pre-wrap break-words rounded-md border border-background-200 bg-background-50 px-3 py-2.5 font-body text-sm text-foreground-800">
            {config.subject || '（未設定）'}
          </pre>
        </div>

        <div>
          <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-foreground-500">
            本文テンプレート
          </p>
          <pre className="whitespace-pre-wrap break-words rounded-md border border-background-200 bg-background-50 px-3 py-2.5 font-body text-sm text-foreground-800">
            {config.message || '（未設定）'}
          </pre>
        </div>

        <p className="text-xs text-foreground-400">
          テンプレートには {'{{email}}'} や {'{{body}}'} などのプレースホルダーが含まれます。
        </p>
      </div>
    </div>
  );
}

interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  required?: boolean;
  placeholder?: string;
}

function Field({ id, label, value, onChange, error, required, placeholder }: FieldProps) {
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-foreground-700">
        {label}
        {required && <span className="ml-1 text-accent-600">*</span>}
      </label>
      <input
        id={id}
        name={id}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full rounded-md border bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 ${
          error ? 'border-accent-400' : 'border-background-300'
        }`}
      />
      {error && <p className="mt-1 text-xs text-accent-700">{error}</p>}
    </div>
  );
}